import { types, flow, Instance, applySnapshot } from 'mobx-state-tree';

import {
  getStripeSecretOnly,
  getStripePlans,
  getPayPalClientId,
  getPayPalPlans,
  processPayment,
  payPalSubscription,
  getDefaultCouponCode,
  updateDefaultCouponCode,
  makeMockPayment,
  getDiscountFromCoupon,
  getPaywallMetadata,
  createPaymentEntity,
  canUserMakePayment,
  finalizePaymentAfterRedirection,
} from './apiWrappers/wrappers/useUserPayment';
import { api } from '../index';
import {
  CreatePaymentEntityResponse,
  DiscountFromCouponResponse,
  FinalizePaymentAfterRedirectionParams,
  FinalizePaymentAfterRedirectionResult,
} from './types/useUserPayment.type';
import { isDefined } from '../utils/isDefined';
import { Plan } from '../types/plan';
import { PaymentComponentMetadata } from './PaymentComponentMetadata';
import { PaywallMeta, PaywallMetaVariants } from '../types/paywall';
import { Variant } from '../components/steps/PickAProduct/PickAProduct.types';

export interface Coupon {
  id: string;
  duration: string;
  name: string;
  percentOff: number;
  amountOff: number;
  valid: boolean;
}

export interface Discount {
  code: string;
  coupon: Coupon;
}

export interface PayPalDiscount {
  code: string;
  coupon: {
    duration: string;
    percentOff: number;
  };
}

export interface PaymentData {
  paymentMethodId: string;
  priceId: string;
  discount: Discount | null;
  meta: {
    choosenPaymentMethod: string;
  };
  onboardingDocumentId: string;
}

export interface PayPalData {
  subscriptionId: string;
  discount: PayPalDiscount;
}

type Plans = Record<string, Plan>;

const areStripePlansLoaded = (plans: Plans) => {
  return isDefined(plans) && Object.keys(plans).length > 0;
};

export const UserPaymentModel = types.model('UserPaymentModel', {});

export const UserPaymentStoreModel = types
  .model('UserPaymentStoreModel', {
    clientSecret: types.string,
    stripePlans: types.frozen<Record<string, Plan>>(),
    payPalPlans: types.frozen(),
    discount: types.maybeNull(types.frozen<Discount>()),
    trialDays: types.number,
    paypalClientId: types.string,
    subscriptionId: types.maybeNull(types.string),
    paymentId: types.string,
    purchaseId: types.maybeNull(types.string),
    planId: types.maybeNull(types.string),
    appliedCouponCode: types.maybeNull(types.string),
    chosenPlanPosition: types.maybeNull(types.string),
    choosenPaymentMethod: types.optional(types.string, ''),
    createAccountOnPay: types.optional(types.boolean, false),
    paymentComponentMetadata: PaymentComponentMetadata,
    payWallMeta: types.maybeNull(types.frozen<PaywallMeta>()),
    enableProgram: types.optional(types.boolean, false),
    hasPlanSearchParam: types.optional(types.boolean, false),
    // withKlarna is for analytics purposes
    withKlarna: types.optional(types.boolean, true),
    // klarnaOnly is to control payment methods type to be only klarna
    klarnaOnly: types.optional(types.boolean, true),
  })
  .actions((self) => ({
    setChosenPlanPosition(planPosition: string) {
      applySnapshot(self, { ...self, chosenPlanPosition: planPosition });
    },

    setWithKlarna(withKlarna: boolean) {
      applySnapshot(self, { ...self, withKlarna });
    },

    setHasPlanSearchParam(hasPlanSearchParam: boolean) {
      applySnapshot(self, { ...self, hasPlanSearchParam });
    },

    setKlarnaOnly(klarnaOnly: boolean) {
      applySnapshot(self, { ...self, klarnaOnly });
    },

    setCreateAccountOnPay(createAccountOnPay: boolean) {
      applySnapshot(self, { ...self, createAccountOnPay });
    },

    setEnableProgram(enableProgram: boolean) {
      applySnapshot(self, { ...self, enableProgram });
    },

    setDiscount(discount: Discount | null) {
      applySnapshot(self, {
        ...self,
        discount,
      });
    },

    getStripePlans: flow(function* fetchData() {
      const { kind, data } = yield getStripePlans();
      if (kind === 'ok') {
        const { plans }: { plans: Record<string, Plan> } = data;

        // filter between programs plans and regular plans if enableProgram flag is on
        const filteredPlans = Object.fromEntries(
          Object.entries(plans).filter(([_, plan]) =>
            self.enableProgram ? plan.metadata?.enableProgram === 'true' : plan.metadata?.enableProgram !== 'true'
          )
        );

        applySnapshot(self, {
          ...self,
          stripePlans: filteredPlans,
        });
      }

      return { kind, data };
    }),

    getPaywallMetadata: flow(function* fetchData(variant?: PaywallMetaVariants) {
      const { kind, data } = yield getPaywallMetadata(variant);
      if (kind === 'ok') {
        applySnapshot(self, {
          ...self,
          payWallMeta: data,
        });
      }

      return { kind, data };
    }),

    getStripeSecretOnly: flow(function* fetchData() {
      const { kind, data } = yield getStripeSecretOnly();
      if (kind === 'ok') {
        const { clientSecret, trialDays = 7 } = data;
        applySnapshot(self, {
          ...self,
          clientSecret,
          trialDays,
        });
      }

      return { kind, data };
    }),

    createPaymentRequest: flow<CreatePaymentEntityResponse, any[]>(function* fetchData(
      planId: string,
      onboardingDocumentId: string,
      couponId?: string,
      klarnaOnly?: boolean
    ) {
      const { kind, data } = yield createPaymentEntity({ planId, onboardingDocumentId, couponId, klarnaOnly });
      if (kind !== 'ok') {
        return { success: false, data };
      }

      if (data.md === true) {
        return { success: true, data };
      }

      const { clientSecret, trialDays = 7 } = data;
      applySnapshot(self, {
        ...self,
        clientSecret,
        trialDays,
      });

      return { success: true, data };
    }),

    applyCustomCouponCode: flow(function* applyCustomCouponCode(couponCode: string) {
      applySnapshot(self, { ...self, appliedCouponCode: couponCode });
      yield updateDefaultCouponCode(couponCode);
    }),

    getDefaultCouponCode: flow(function* fetchData() {
      const { kind, data } = yield getDefaultCouponCode();

      if (kind === 'ok') {
        if (data?.registrantId) {
          api.setAuthorizationHeader({ registrantId: data.registrantId });
        }
        applySnapshot(self, { ...self, appliedCouponCode: data.code });
        yield updateDefaultCouponCode(data.code);
      }
      return { kind, data };
    }),
    getDiscountFromPromoCode: flow<{ valid: true; discount: Discount } | { valid: false; error: any }, any[]>(
      function* fetchData(promoCode: string) {
        const { kind, data } = yield getDiscountFromCoupon(promoCode);

        const dataWithProperType = data as DiscountFromCouponResponse;

        if (kind === 'ok' && dataWithProperType.code) {
          return { valid: true, discount: dataWithProperType };
        }

        return { valid: false, error: data };
      }
    ),
    setPlanId: (planId: string) => {
      applySnapshot(self, { ...self, planId });
    },
    payPalSubscription: flow(function* postData(payPalData: PayPalData) {
      const { kind, data } = yield payPalSubscription(payPalData);
      if (kind === 'ok') {
        applySnapshot(self, { ...self, subscriptionId: data.subscriptionDetails.id });
      }
      return kind === 'ok' ? true : false;
    }),
    makeMockPayment: flow(function* postData() {
      const { kind } = yield makeMockPayment();
      return kind === 'ok' ? true : false;
    }),
    processPayment: flow(function* postData(paymentData: PaymentData) {
      const { kind, data } = yield processPayment(paymentData);
      if (kind !== 'ok') {
        return { success: false, data };
      }

      if (data.md === true) {
        return { success: true, isMagicDoorPurchase: true, data };
      }

      const { purchaseId, paymentId } = data;
      applySnapshot(self, {
        ...self,
        purchaseId,
        paymentId,
        planId: paymentData.priceId,
        choosenPaymentMethod: paymentData.meta.choosenPaymentMethod,
      });

      return { kind, data };
    }),
    getPayPalPlans: flow(function* fetchData() {
      const { kind, data } = yield getPayPalPlans();
      if (kind === 'ok') {
        const { trialDays = 7, activePlans } = data;
        applySnapshot(self, { ...self, payPalPlans: activePlans ?? null, trialDays });
      }
      return { kind, data };
    }),
    getPayPalClientId: flow(function* fetchData() {
      const { kind, data } = yield getPayPalClientId();
      if (kind === 'ok') {
        const { paypalClientId } = data;
        applySnapshot(self, { ...self, paypalClientId });
      }
      return { kind, data };
    }),
    canUserMakePayment: flow(function* fetchData() {
      const { kind, data } = yield canUserMakePayment();
      return {
        result: kind === 'ok',
        data,
      };
    }),
    finalizePaymentAfterRedirection: flow<FinalizePaymentAfterRedirectionResult, any[]>(function* fetchData(
      params: FinalizePaymentAfterRedirectionParams
    ) {
      const { kind, data } = yield finalizePaymentAfterRedirection(params);

      if (kind === 'ok') {
        return {
          success: true,
          data,
        };
      }

      return {
        success: false,
        paymentAlreadyExists: kind === 'forbidden',
        data,
      };
    }),
  }))
  .actions((self) => ({
    loadPaymentMetaIfNeed: async (planId: string | null) => {
      const findPlanPositionById = (planId: string, plans: Record<string, any>) => {
        for (let planPosition in plans) {
          if (plans[planPosition].id === planId) {
            return planPosition;
          }
        }
        // if we do not find plan by planId, we return the yearly plan as default
        for (let planPosition in plans) {
          if (plans[planPosition].interval === 'year') {
            return planPosition;
          }
        }
        return null;
      };

      if (areStripePlansLoaded(self.stripePlans)) {
        return;
      }

      await self.getStripePlans();
      await self.getPaywallMetadata();

      if (!planId) {
        return;
      }

      const planPosition = findPlanPositionById(planId, self.stripePlans);
      if (planPosition) {
        self.setChosenPlanPosition(planPosition);
      }
    },
  }))
  .views((self) => ({
    getChoosenPlan: (): null | Plan => {
      if (!isDefined(self.chosenPlanPosition) || !areStripePlansLoaded(self.stripePlans)) {
        return null;
      }
      return self.stripePlans[self.chosenPlanPosition];
    },
    checkIfPlansAreLoaded: () => {
      return areStripePlansLoaded(self.stripePlans);
    },
    // used for PickAProgram component where we care about "a" plan
    get planPositions(): string[] {
      return Object.entries(self.stripePlans)
        .filter(([_key, plan]) => plan.metadata.showInPlansList === 'true')
        .map(([key]) => key);
    },
    // used for PickAProduct component, that has different versions of products for a/b testing
    getPlanPositions(variant = Variant.v1): string[] {
      return Object.entries(self.stripePlans)
        .filter(
          ([_key, plan]) => plan.metadata.showInPlansList === 'true' && plan.metadata.useOnlyInVariant === variant
        )
        .map(([key]) => key);
    },
  }));

export interface UserPayment extends Instance<typeof UserPaymentModel> {}
